import type {
  EditorToRendererMessage,
  EditorToRendererMessageType,
  RendererToEditorMessage,
} from "@lureo/editor-contracts";
import type { Observable } from "rxjs";
import { ALLOWED_PARENT_ORIGINS, TEMPLATE_MESSAGE_TYPES } from "./allowedParents";
import { RendererBridgeCore } from "./RendererBridgeCore";
import { RENDERER_ACCEPTS } from "./rendererAccepts";
import {
  silentBridgeLogger,
  type EditorMessage,
  type RendererBridgeOptions,
  type RendererBridgePort,
} from "./RendererBridgePort";

export interface BridgeMessageEvent {
  readonly source: unknown;
  readonly origin: string;
  readonly data: unknown;
}

export interface BridgeParent {
  postMessage(message: unknown, targetOrigin: string): void;
}

/** The page the renderer runs in: the iframe's window. */
export interface BridgeWindow {
  readonly parent: BridgeParent | null;
  readonly location: { readonly origin: string };
  readonly document: { readonly referrer: string };
  addEventListener(type: "message", listener: (event: BridgeMessageEvent) => void): void;
  removeEventListener(type: "message", listener: (event: BridgeMessageEvent) => void): void;
}

/** The bridge to the editor page that frames the renderer, over window.postMessage. */
export class PostMessageRendererBridge implements RendererBridgePort {
  readonly #core: RendererBridgeCore;
  #initialized = false;
  #parentOrigin: string | null = null;
  #listening = false;

  readonly accepts = RENDERER_ACCEPTS;

  constructor(
    private readonly win: BridgeWindow,
    options: RendererBridgeOptions = {},
  ) {
    this.#core = new RendererBridgeCore(options.logger ?? silentBridgeLogger, options.contractCheck);
  }

  get received$(): Observable<EditorToRendererMessage> {
    return this.#core.received$;
  }

  /** The origin sends go to; null until a trusted referrer or the editor's first message sets it. */
  get parentOrigin(): string | null {
    return this.#parentOrigin;
  }

  on<T extends EditorToRendererMessageType>(type: T): Observable<EditorMessage<T>> {
    return this.#core.on(type);
  }

  init(): void {
    if (this.#initialized) return;
    this.#initialized = true;

    if (!this.#framed()) return;

    try {
      if (this.win.document.referrer !== "") {
        const refOrigin = new URL(this.win.document.referrer).origin;
        if (refOrigin === this.win.location.origin || ALLOWED_PARENT_ORIGINS.includes(refOrigin)) {
          this.#parentOrigin = refOrigin;
        }
      }
    } catch {
      // No trusted parent yet: sends wait for its first message.
    }

    this.win.addEventListener("message", this.#onMessage);
    this.#listening = true;
  }

  send(message: RendererToEditorMessage): void {
    const json = this.#core.serialize(message);
    const parent = this.win.parent;
    if (this.#parentOrigin === null || parent === null || !this.#framed()) return;
    try {
      parent.postMessage(JSON.parse(json), this.#parentOrigin);
    } catch {
      // Best effort: a message the editor cannot receive is dropped.
    }
  }

  dispose(): void {
    if (this.#listening) this.win.removeEventListener("message", this.#onMessage);
    this.#listening = false;
    this.#core.complete();
  }

  #framed(): boolean {
    const parent = this.win.parent;
    return parent !== null && (parent as unknown) !== this.win;
  }

  readonly #onMessage = (event: BridgeMessageEvent): void => {
    if (event.source !== this.win.parent) return;
    const trusted =
      event.origin === "" || event.origin === this.win.location.origin || ALLOWED_PARENT_ORIGINS.includes(event.origin);
    if (!trusted) return;
    const data = event.data;
    if (typeof data !== "object" || data === null) return;
    const type = (data as Readonly<Record<string, unknown>>)["type"];
    if (typeof type !== "string") return;
    if (TEMPLATE_MESSAGE_TYPES.includes(type)) return;
    this.#parentOrigin ??= event.origin === "" ? this.win.location.origin : event.origin;
    let json: string;
    try {
      json = JSON.stringify(data);
    } catch {
      return;
    }
    this.#core.receive(json);
  };
}
